/**
 * Where the browser Sentry SDK may run: exactly where MaiTeams' own analytics
 * may, judged by the same predicate (`isPostHogSurface`, over
 * `lib/analytics/first-party-tracking-scope.ts`). A capability URL
 * (`/q/<token>`, `/demo/s/<token>`, `/public/preferences/<token>`, the legacy
 * unsubscribe links, …) is a credential handed to someone who never agreed to
 * anything, and a customer's published page is the customer's traffic, so
 * neither sends MaiTeams an error, a trace, a profile or a log.
 *
 * `instrumentation-client.ts` uses these in three places, the PostHog shape:
 *   - at page load, `sentryMayRun` decides whether `Sentry.init` runs at all,
 *     and on the first client-side navigation that lands on an allowed page
 *     (`pageLocationOf` reads the target) it starts the SDK a page that loaded
 *     off-surface never started;
 *   - for the life of a page that did start it, the `before*` hooks drop what
 *     would leave while the browser is on an off-surface page (the demo flow
 *     pushes `/demo/s/<token>` client-side), and any event, transaction or
 *     breadcrumb whose URL points at one, so a later on-surface error does not
 *     carry the token page along in its breadcrumbs;
 *   - `gateTransport` wraps the transport, the one chokepoint every envelope
 *     crosses, so what the hooks do not see (sessions, client reports) is
 *     dropped there while the browser is on an off-surface page.
 *
 * Pure apart from the default `here`, which reads `window.location`.
 */
import type { BrowserOptions } from '@sentry/nextjs'
import { isCapabilityPath, isCredentialBearingUrl } from './first-party-tracking-scope'
import { isPostHogSurface, type PageLocation } from './posthog-client'

/** The page the browser is on right now, or null outside a browser. */
function currentPageLocation(): PageLocation | null {
  if (typeof location === 'undefined' || !location?.hostname) return null
  return { hostname: location.hostname, pathname: location.pathname, search: location.search }
}

/** Where a navigation is heading, read from an href against the current URL; null when it does not parse. */
export function pageLocationOf(href: string, base: string): PageLocation | null {
  try {
    const { hostname, pathname, search } = new URL(href, base)
    return { hostname, pathname, search }
  } catch {
    return null
  }
}

/** True when the browser Sentry SDK may run on this page. No location (not a browser) is a no. */
export function sentryMayRun(page: PageLocation | null): boolean {
  return page !== null && isPostHogSurface(page)
}

/**
 * True when a URL or path points at a capability or credential page, on any
 * host. A value that is not a string, or does not parse, points nowhere.
 */
export function pointsOffSurface(value: unknown, base = 'https://app.invalid'): boolean {
  if (typeof value !== 'string' || value.length === 0) return false
  let target: URL
  try {
    target = new URL(value, base)
  } catch {
    return false
  }
  return isCapabilityPath(target.pathname) || isCredentialBearingUrl(target.pathname, target.search)
}

type WithRequestUrl = { request?: { url?: string } }

/** `beforeSend` / `beforeSendTransaction`: drop off-surface, pass everything else unchanged. */
export function keepOnSurface<T extends WithRequestUrl>(
  event: T,
  here: () => PageLocation | null = currentPageLocation
): T | null {
  if (!sentryMayRun(here())) return null
  if (pointsOffSurface(event.request?.url)) return null
  return event
}

type BreadcrumbLike = { data?: Record<string, unknown> }

/** `beforeBreadcrumb`: drop one recorded off-surface, or whose from/to/url is a capability or credential page. */
export function keepBreadcrumbOnSurface<T extends BreadcrumbLike>(
  breadcrumb: T,
  here: () => PageLocation | null = currentPageLocation
): T | null {
  if (!sentryMayRun(here())) return null
  const data = breadcrumb.data
  if (data && (pointsOffSurface(data.from) || pointsOffSurface(data.to) || pointsOffSurface(data.url))) return null
  return breadcrumb
}

/** `beforeSendLog`: drop a log written while the browser is on an off-surface page. */
export function keepLogOnSurface<T>(log: T, here: () => PageLocation | null = currentPageLocation): T | null {
  return sentryMayRun(here()) ? log : null
}

type SentryTransportFactory = NonNullable<BrowserOptions['transport']>

/**
 * A transport that sends nothing while the browser is on an off-surface page.
 * The dropped envelope resolves as an accepted send with no rate-limit
 * headers, so the SDK does not back off. `flush` stays the inner transport's.
 */
export function gateTransport(
  make: SentryTransportFactory,
  here: () => PageLocation | null = currentPageLocation,
  analyticsAllowed: () => boolean = () => false
): SentryTransportFactory {
  return (options) => {
    const inner = make(options)
    return {
      ...inner,
      send: (envelope) => {
        if (!sentryMayRun(here())) return Promise.resolve({})
        if (analyticsAllowed()) return inner.send(envelope)
        const items = envelope[1].filter(([header]) => !['transaction', 'profile', 'profile_chunk'].includes(header.type))
        return items.length ? inner.send([envelope[0], items] as typeof envelope) : Promise.resolve({})
      },
    }
  }
}
